/// <reference path="../../../types/vlitejs.d.ts" />

"use client";

import {
  type ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
} from "react";
import Vlitejs from "vlitejs";
import type {
  VliteEventHandlers,
  VliteInstance,
  VlitePlayerProps,
  VlitePlayerType,
  VlitePlugin,
} from "./types";

type VliteMediaElement = HTMLVideoElement | HTMLAudioElement | HTMLDivElement;
type MediaPreload = "none" | "metadata" | "auto";

interface VliteMediaConfig {
  className?: string;
  providerId: string;
  src?: string;
  style?: VlitePlayerProps["style"];
  type?: VlitePlayerProps["type"];
  videoId?: string;
}

const EVENT_TO_HANDLER: Record<keyof VliteEventHandlers, string> = {
  onPlay: "play",
  onPause: "pause",
  onProgress: "progress",
  onTimeUpdate: "timeupdate",
  onVolumeChange: "volumechange",
  onSourceChange: "sourcechange",
  onEnterFullscreen: "enterfullscreen",
  onExitFullscreen: "exitfullscreen",
  onEnded: "ended",
};

function getProviderId(provider: VlitePlayerProps["provider"]): string {
  if (typeof provider === "string") {
    return provider;
  }

  return provider?.id ?? "html5";
}

function registerInlineProvider(provider: VlitePlayerProps["provider"]): void {
  if (provider && typeof provider !== "string") {
    const vlite = Vlitejs as unknown as {
      registerProvider?: (
        id: string,
        entry: unknown,
        options?: Record<string, unknown>
      ) => void;
    };

    vlite.registerProvider?.(
      provider.id,
      provider.entry,
      provider.options as Record<string, unknown> | undefined
    );
  }
}

function registerInlinePlugins(plugins: VlitePlugin[] | undefined): string[] {
  if (!plugins) {
    return [];
  }

  const pluginIds: string[] = [];
  for (const plugin of plugins) {
    if (typeof plugin === "string") {
      pluginIds.push(plugin);
      continue;
    }

    const vlite = Vlitejs as unknown as {
      registerPlugin?: (
        id: string,
        entry: unknown,
        options?: Record<string, unknown>
      ) => void;
    };

    vlite.registerPlugin?.(
      plugin.id,
      plugin.entry,
      plugin.options as Record<string, unknown> | undefined
    );
    pluginIds.push(plugin.id);
  }

  return pluginIds;
}

function getOptionsKey(options: VlitePlayerProps["options"]): string {
  if (!options) {
    return "";
  }

  try {
    return JSON.stringify(options);
  } catch {
    return "";
  }
}

function getPluginsKey(plugins: VlitePlayerProps["plugins"]): string {
  if (!plugins) {
    return "";
  }

  return plugins
    .map((plugin) => (typeof plugin === "string" ? plugin : plugin.id))
    .join(",");
}

function getMediaPreload(options: VlitePlayerProps["options"]): MediaPreload {
  return options?.preload ?? (options?.autoplay ? "auto" : "metadata");
}

function applyVideoPlaybackAttributes(
  video: HTMLVideoElement,
  options: VlitePlayerProps["options"]
) {
  video.preload = getMediaPreload(options);

  if (options?.playsinline !== false) {
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
  }

  if (options?.loop) {
    video.loop = true;
  }

  if (options?.autoplay) {
    video.autoplay = true;
  }

  if (options?.muted || options?.autoplay) {
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("muted", "");
  }

  if (options?.poster) {
    video.poster = options.poster;
  }
}

function createVliteMediaElement(
  config: VliteMediaConfig,
  options: VlitePlayerProps["options"]
): VliteMediaElement {
  if (config.providerId !== "html5") {
    const providerElement = document.createElement("div");
    const providerAttribute = `data-${config.providerId}-id`;
    if (config.videoId) {
      providerElement.setAttribute(providerAttribute, config.videoId);
    }
    return providerElement;
  }

  if (config.type === "audio") {
    const audio = document.createElement("audio");
    if (config.src) {
      audio.src = config.src;
    }
    return audio;
  }

  const video = document.createElement("video");
  applyVideoPlaybackAttributes(video, options);
  if (config.src) {
    video.src = config.src;
  }
  return video;
}

function applyMediaProps(element: VliteMediaElement, config: VliteMediaConfig) {
  if (config.className) {
    element.className = config.className;
  }
  if (config.style) {
    Object.assign(element.style, config.style);
  }
}

function VlitePlayerComponent(
  props: Readonly<VlitePlayerProps>,
  ref: ForwardedRef<VliteInstance>
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const handlersRef = useRef({
    onPlay: props.onPlay,
    onPause: props.onPause,
    onProgress: props.onProgress,
    onTimeUpdate: props.onTimeUpdate,
    onVolumeChange: props.onVolumeChange,
    onSourceChange: props.onSourceChange,
    onEnterFullscreen: props.onEnterFullscreen,
    onExitFullscreen: props.onExitFullscreen,
    onEnded: props.onEnded,
    onReady: props.onReady,
  });

  useEffect(() => {
    handlersRef.current = {
      onPlay: props.onPlay,
      onPause: props.onPause,
      onProgress: props.onProgress,
      onTimeUpdate: props.onTimeUpdate,
      onVolumeChange: props.onVolumeChange,
      onSourceChange: props.onSourceChange,
      onEnterFullscreen: props.onEnterFullscreen,
      onExitFullscreen: props.onExitFullscreen,
      onEnded: props.onEnded,
      onReady: props.onReady,
    };
  });

  const providerId = getProviderId(props.provider);
  const mediaConfig = useMemo<VliteMediaConfig>(
    () => ({
      className: props.className,
      providerId,
      src: props.src,
      style: props.style,
      type: props.type,
      videoId: props.videoId,
    }),
    [
      providerId,
      props.className,
      props.src,
      props.style,
      props.type,
      props.videoId,
    ]
  );
  const optionsCacheRef = useRef({
    key: getOptionsKey(props.options),
    value: props.options,
  });
  const nextOptionsKey = getOptionsKey(props.options);
  if (optionsCacheRef.current.key !== nextOptionsKey) {
    optionsCacheRef.current = { key: nextOptionsKey, value: props.options };
  }
  const stableOptions = optionsCacheRef.current.value;

  const pluginsCacheRef = useRef({
    key: getPluginsKey(props.plugins),
    value: props.plugins,
  });
  const nextPluginsKey = getPluginsKey(props.plugins);
  if (pluginsCacheRef.current.key !== nextPluginsKey) {
    pluginsCacheRef.current = { key: nextPluginsKey, value: props.plugins };
  }
  const stablePlugins = pluginsCacheRef.current.value;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    registerInlineProvider(props.provider);
    const pluginIds = registerInlinePlugins(stablePlugins);
    const mediaElement = createVliteMediaElement(mediaConfig, stableOptions);
    applyMediaProps(mediaElement, mediaConfig);
    host.appendChild(mediaElement);

    const player = new Vlitejs(mediaElement, {
      options: stableOptions as never,
      provider: providerId,
      plugins: pluginIds,
      onReady: (instance: unknown) => {
        handlersRef.current.onReady?.(instance as VlitePlayerType);
      },
    }) as unknown as VliteInstance;

    if (typeof ref === "function") {
      ref(player);
    } else if (ref) {
      ref.current = player;
    }

    const playerContainer = player.container;
    const listeners = new Map<string, EventListener>();

    for (const [handlerName, eventName] of Object.entries(EVENT_TO_HANDLER)) {
      const listener = (event: Event) => {
        const handler =
          handlersRef.current[handlerName as keyof VliteEventHandlers];
        if (typeof handler === "function") {
          handler(event);
        }
      };

      listeners.set(eventName, listener);
      playerContainer.addEventListener(eventName, listener);
    }

    return () => {
      for (const [eventName, listener] of listeners) {
        playerContainer.removeEventListener(eventName, listener);
      }

      player.destroy();
      host.replaceChildren();

      if (typeof ref === "function") {
        ref(null);
      } else if (ref) {
        ref.current = null;
      }
    };
  }, [
    providerId,
    ref,
    mediaConfig,
    props.provider,
    stableOptions,
    stablePlugins,
  ]);

  return <div ref={hostRef} />;
}

export const VlitePlayer = forwardRef(VlitePlayerComponent);
VlitePlayer.displayName = "VlitePlayer";

// biome-ignore lint/style/noDefaultExport: preserved for backwards compatibility with existing imports.
export default VlitePlayer;
